
import { supabase } from "@/integrations/supabase/client";
import logger from "@/lib/logger";
import { toast } from "@/hooks/use-toast";
import { checkInternetConnection } from "@/lib/network-utils";

interface ReviewProfileResult {
  review: string;
  error: string | null;
  isQuotaExceeded: boolean;
  isRateLimited: boolean;
}

// Function to record usage
const recordUsage = async () => {
  try {
    const { data: authData } = await supabase.auth.getUser();
    if (!authData?.user?.id) return;
    
    await supabase
      .from('profile_review_usage')
      .insert({
        model: 'gpt-4o-mini',
        user_id: authData.user.id
      });
      
    logger.log("Profile review usage recorded successfully");
  } catch (err) {
    logger.error("Failed to record usage:", err);
  }
};

export const reviewProfileApi = async (profileText: string): Promise<ReviewProfileResult> => {
  logger.log("Starting profile review API call");
  
  try {
    // Show initial toast
    toast({
      title: "Analyzing your profile...",
      description: "This may take a few moments.",
      duration: 3000,
    });
    
    // Enhanced connectivity check with multiple attempts
    logger.log("Performing comprehensive connectivity check");
    let isConnected = false;
    
    // Try 3 times with increasing timeouts
    isConnected = await checkInternetConnection(3000) || 
                  await checkInternetConnection(8000) || 
                  await checkInternetConnection(15000);
    
    if (!isConnected) {
      logger.error("All connectivity checks failed - definitely offline");
      toast({
        title: "Connection Problem",
        description: "We couldn't connect to the internet. Please check your connection and try again.",
        variant: "destructive",
      });
      
      return {
        review: "",
        error: "Internet connection not detected. Please check your network settings and try again.",
        isQuotaExceeded: false,
        isRateLimited: false
      };
    }
    
    logger.log("Internet connection confirmed, calling edge function");
    
    // Improved timeout and retry handling
    const timeoutDuration = 90000; // 1.5 minutes
    const maxRetries = 2;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        if (attempt > 0) {
          logger.log(`Retry attempt ${attempt} of ${maxRetries}`);
          toast({
            title: `Retrying (${attempt}/${maxRetries})`,
            description: "Still analyzing your profile...",
            duration: 3000,
          });
          
          // Wait with exponential backoff
          await new Promise(resolve => setTimeout(resolve, 2000 * Math.pow(2, attempt - 1)));
        }
        
        // Make the function call
        const { data, error } = await supabase.functions.invoke("review-profile", {
          body: { profileText: profileText.trim() }
        });
        
        if (error) {
          throw error;
        }
        
        // Handle errors from the response data
        if (data.error) {
          logger.error("Profile review error from edge function:", data.error);
          
          if (data.quota_exceeded) {
            return {
              review: "",
              error: data.error,
              isQuotaExceeded: true,
              isRateLimited: false
            };
          } else if (data.rate_limited) {
            return {
              review: "",
              error: data.error,
              isQuotaExceeded: false,
              isRateLimited: true
            };
          } else {
            return {
              review: "",
              error: data.error,
              isQuotaExceeded: false,
              isRateLimited: false
            };
          }
        }
        
        // Success case
        if (data.review) {
          logger.log("Profile review generated successfully");
          await recordUsage();
          
          toast({
            title: "Profile review complete!",
            description: "We've analyzed your profile and provided feedback.",
            duration: 3000,
          });
          
          return {
            review: data.review,
            error: null,
            isQuotaExceeded: false,
            isRateLimited: false
          };
        } else {
          // No review in the response
          throw new Error("No review content was returned from the service");
        }
      } catch (retryError) {
        if (attempt >= maxRetries) {
          throw retryError;
        }
        logger.error(`Attempt ${attempt + 1} failed:`, retryError.message);
      }
    }
    
    // This should not be reached due to the throw above
    return {
      review: "",
      error: "All retry attempts failed",
      isQuotaExceeded: false,
      isRateLimited: false
    };
  } catch (error) {
    logger.error("Unexpected error in profile review:", error);
    
    let errorMessage = "An unexpected error occurred";
    
    if (error.message?.includes("timed out") || error.message?.includes("aborted")) {
      errorMessage = "Request timed out. The service might be busy, please try again later.";
      toast({
        title: "Request Timeout",
        description: errorMessage,
        variant: "destructive",
      });
    } else if (
      error.message?.includes("network") || 
      error.message?.includes("fetch") ||
      error.message?.includes("connect")
    ) {
      errorMessage = "Network connection error. Please check your internet and try again.";
      toast({
        title: "Network Error",
        description: errorMessage,
        variant: "destructive",
      });
    } else {
      // Use the actual error message
      errorMessage = error.message || "An unexpected error occurred";
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    }
    
    return {
      review: "",
      error: errorMessage,
      isQuotaExceeded: false,
      isRateLimited: false
    };
  }
};
